import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Spin,
  Select,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";

const { Option } = Select;

const FILTERS_STORAGE_KEY = "report:sales-order-entry:filters";

const resolveSalesOrderId = (row = {}) => {
  if (row.soId) return row.soId;
  if (row.salesOrderId) {
    return typeof row.salesOrderId === "object"
      ? row.salesOrderId._id || row.salesOrderId.id
      : row.salesOrderId;
  }
  if (row._id) return row._id;
  if (row.id) return row.id;
  return null;
};

const SalesOrderEntryReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Restore filters on mount (Back navigation / refresh).
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      if (typeof parsed.financialYear === "string" && parsed.financialYear) {
        setFinancialYear(parsed.financialYear);
      }
    } catch (e) {
      // Ignore bad JSON / storage issues
    } finally {
      setFiltersHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({ financialYear }),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [filtersHydrated, financialYear]);

  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (isExcel = false) => {
      const isCurrent = isExcel ? () => true : beginRequest();
      if (isExcel) setDownloading(true);
      else {
        setLoading(true);
        setRows([]);
      }
      try {
        const params = new URLSearchParams();
        params.set("financialYear", financialYear);
        if (isExcel) params.set("excel", "true");
        const q = `?${params.toString()}`;
        const url = `${apiBaseUrl}/inventoryManagement/getSalesOrderEntryReport${q}`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (isExcel) {
          if (!res.ok) {
            message.error("Failed to download Excel");
            return;
          }
          const blob = await res.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = `Sales_Order_Entry_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
          return;
        }

        const j = await res.json();
      if (!isCurrent()) return;
        if (j.success) {
          setRows(
            (j.data || []).map((row) => ({
              ...row,
              soId: resolveSalesOrderId(row),
            })),
          );
        } else {
          console.error("getSalesOrderEntryReport failed:", j.message);
          message.error("Failed to fetch sales order entry report");
        }
      } catch (err) {
        console.error(err);
        message.error(isExcel ? "Error downloading Excel" : "Error fetching report");
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setDownloading(false);
        }
      }
    },
    [apiBaseUrl, financialYear],
  );

  useEffect(() => {
    if (!filtersHydrated) return;
    fetchData(false);
  }, [fetchData, filtersHydrated]);

  const handleDownloadExcel = () => {
    fetchData(true);
  };

  const columns = useMemo(
    () => [
      {
        title: "SO No.",
        dataIndex: "soNo",
        key: "soNo",
        width: 120,
        align: "center",
        render: (v, record) => {
          const soId = record?.soId;
          if (!v || !soId) {
            return <span>{v || "-"}</span>;
          }

          return (
            <span
              role="link"
              tabIndex={0}
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/sales-order/${soId}?tab=1`);
                }
              }}
              title="Open Sales Order"
            >
              {v}
            </span>
          );
        },
      },
      {
        title: "SO Date",
        dataIndex: "soDate",
        key: "soDate",
        width: 120,
        align: "center",
        render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
      },
      {
        title: "Customer",
        dataIndex: "customer",
        key: "customer",
        width: 220,
        align: "left",
      },
      {
        title: "HO Amount",
        dataIndex: "hoAmount",
        key: "hoAmount",
        width: 120,
        align: "right",
        render: (v) =>
          typeof v === "number"
            ? new Intl.NumberFormat("en-IN").format(v)
            : (v ?? "-"),
      },
      {
        title: "PRN Amount",
        dataIndex: "prnAmount",
        key: "prnAmount",
        width: 120,
        align: "right",
        render: (v) =>
          typeof v === "number"
            ? new Intl.NumberFormat("en-IN").format(v)
            : (v ?? "-"),
      },
      {
        title: "Salesman",
        dataIndex: "salesman",
        key: "salesman",
        width: 140,
        align: "left",
      },
      {
        title: "Brand",
        dataIndex: "brand",
        key: "brand",
        width: 160,
        align: "left",
      },
    ],
    [navigate],
  );

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Sales Order Entry
            </Space>

            <Space>
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No data found
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r, i) => r.soNo?.toString() || `row-${i}`}
            pagination={false}
            scroll={{ y: 500 }}
            components={components}
            sticky
          />
        )}
      </Card>
    </div>
  );
};

export default SalesOrderEntryReport;
